import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  canFeatureForRole,
  hasAnyPermissionForModule,
  hasAssetsInventoryModuleAccess,
  hasPeopleModuleAccess,
  hasProjectManagementModuleAccess,
  PROJECT_MANAGEMENT_PROJECT_FEATURES,
  PROJECT_MANAGEMENT_TASK_FEATURES,
  PERMISSIONS,
} from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { getProjectManagementMenuGroup } from '../../modules/projectManagement/config';
import api from '../../utils/api';

const ADMIN_MENU_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/admin/teams-events', label: 'Teams', icon: '🎉' },
  { path: '/admin/matches', label: 'Matches', icon: '🎯' },
  { path: '/admin/assets', label: 'Assets', icon: '📦' },
  { path: '/admin/checkout', label: 'Check-Out', icon: '📤' },
  { path: '/admin/checkin', label: 'Check-In', icon: '📥' },
  { path: '/admin/my-asset-handover', label: 'My Asset Handover', icon: '📋' },
  { path: '/admin/my-assets', label: 'My Assets', icon: '📦' },
  { path: '/admin/acknowledgements', label: 'Acknowledgements', icon: '✅' },
  { path: '/admin/people', label: 'People', icon: '👥' },
  { path: '/admin/reports', label: 'Reports', icon: '📈' },
  { path: '/admin/notifications', label: 'Notifications', icon: '🔔' },
  { path: '/admin/trash', label: 'Trash', icon: '🗑️' },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
];

const EMPLOYEE_MENU_ITEMS = [
  { path: '/employee/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/employee/teams-events', label: 'Teams', icon: '🎉' },
  { path: '/employee/matches', label: 'Matches', icon: '🎯' },
  { path: '/employee/assets', label: 'Assets', icon: '📦' },
  { path: '/employee/my-asset-handover', label: 'My Asset Handover', icon: '📋' },
  { path: '/employee/my-assets', label: 'My Assets', icon: '📦' },
  { path: '/employee/people', label: 'People', icon: '👥' },
  { path: '/employee/reports', label: 'Reports', icon: '📈' },
  { path: '/employee/profile', label: 'Profile', icon: '👤' },
  { path: '/employee/notifications', label: 'Notifications', icon: '🔔' },
];

const Sidebar = ({ userRole, isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Re-read when the route changes so menu permissions match after login / same-tab role updates.
  const sessionRole = useMemo(() => {
    void location.pathname;
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return u?.role;
    } catch (error) {
      return '';
    }
  }, [location.pathname]);

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const { loading: referenceDataLoading } = useReferenceData();
  const [openGroups, setOpenGroups] = useState({});
  // True once we've confirmed at least one user reports to the current user (direct report).
  // Used (with the View All Tasks permission) to decide if the Summary Report menu shows.
  const [hasSubordinates, setHasSubordinates] = useState(false);

  const currentUserId = useMemo(() => {
    void location.pathname;
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.id || u?._id || '').trim();
    } catch (error) {
      return '';
    }
  }, [location.pathname]);

  // Detect whether the current user has any direct report. A user with indirect reports
  // always has a direct report too, so a direct-report check is enough for menu visibility.
  useEffect(() => {
    let cancelled = false;
    const checkSubordinates = async () => {
      if (!currentUserId) {
        setHasSubordinates(false);
        return;
      }
      try {
        const res = await api.getUsers({ limit: 1000, isActive: 'true' });
        const users = res?.success ? res.data || [] : [];
        const found = users.some((u) => {
          const managerId = String(u.manager?._id || u.manager?.id || u.manager || '');
          const uid = String(u._id || u.id || '');
          return managerId === currentUserId && uid !== currentUserId;
        });
        if (!cancelled) setHasSubordinates(found);
      } catch (error) {
        if (!cancelled) setHasSubordinates(false);
      }
    };
    checkSubordinates();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const normalizedSessionRole = useMemo(
    () =>
      String(sessionRole || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, ''),
    [sessionRole]
  );

  const menuItems = useMemo(() => {
    const base = userRole === 'admin' ? [...ADMIN_MENU_ITEMS] : [...EMPLOYEE_MENU_ITEMS];
    const reportsIndex = base.findIndex((item) => item.path?.includes('/reports'));
    const insertAt = reportsIndex >= 0 ? reportsIndex : base.length;

    // Project Management shows only when the role has at least one Project or Task permission.
    // Projects link → any project permission; Tasks link → any task permission.
    const projectGroup = getProjectManagementMenuGroup(userRole);
    if (!permissionsReady) {
      base.splice(insertAt, 0, projectGroup);
    } else if (hasProjectManagementModuleAccess(normalizedSessionRole, rolePermissions)) {
      const children = projectGroup.children.filter((child) => {
        if (child.path.endsWith('/tasks')) {
          return canFeatureForRole(normalizedSessionRole, rolePermissions, PROJECT_MANAGEMENT_TASK_FEATURES);
        }
        if (child.path.endsWith('/projects')) {
          return canFeatureForRole(normalizedSessionRole, rolePermissions, PROJECT_MANAGEMENT_PROJECT_FEATURES);
        }
        if (child.path.endsWith('/summary-report')) {
          // Summary Report shows only if the user can see all tasks OR has at least one report.
          const canViewAllTasks = canFeatureForRole(normalizedSessionRole, rolePermissions, [PERMISSIONS.VIEW_ALL_TASKS]);
          return canViewAllTasks || hasSubordinates;
        }
        return true;
      });
      if (children.length > 0) {
        base.splice(insertAt, 0, { ...projectGroup, children });
      }
    }

    return base.filter((item) => {
      if (item.type === 'group' && Array.isArray(item.children)) {
        return true;
      }

      if (!permissionsReady || referenceDataLoading) {
        if (item.path?.includes('/teams-events') || item.path?.includes('/matches')) {
          return false;
        }
        return true;
      }

      if (item.path?.includes('/teams-events')) {
        return hasAnyPermissionForModule(normalizedSessionRole, rolePermissions, 'Teams');
      }
      if (item.path?.includes('/matches')) {
        return hasAnyPermissionForModule(normalizedSessionRole, rolePermissions, 'Matches');
      }

      if (item.path === '/employee/assets') {
        return hasAssetsInventoryModuleAccess(normalizedSessionRole, rolePermissions);
      }

      if (item.path === '/employee/people') {
        return hasPeopleModuleAccess(normalizedSessionRole, rolePermissions);
      }

      if (item.path === '/employee/reports') {
        return hasAnyPermissionForModule(normalizedSessionRole, rolePermissions, 'Reports');
      }

      // My Assets list — My Assets module only (view_assigned_assets)
      if (item.path === '/employee/my-assets' || item.path === '/admin/my-assets') {
        return canFeatureForRole(normalizedSessionRole, rolePermissions, [
          PERMISSIONS.VIEW_ASSIGNED_ASSETS,
        ]);
      }

      // My Asset Handover — Asset Handover module only (view_my_asset_handover)
      if (item.path === '/employee/my-asset-handover' || item.path === '/admin/my-asset-handover') {
        return canFeatureForRole(normalizedSessionRole, rolePermissions, [
          PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
        ]);
      }

      if (userRole !== 'admin') {
        return true;
      }

      if (item.path === '/admin/assets') {
        return hasAssetsInventoryModuleAccess(normalizedSessionRole, rolePermissions);
      }
      if (item.path === '/admin/checkout' || item.path === '/admin/checkin') {
        return hasAssetsInventoryModuleAccess(normalizedSessionRole, rolePermissions);
      }
      // Same rule as TrashPage: the page lists assets / projects / tasks, and opens when the
      // user can delete at least one of them. Gating the link on delete_assets alone hid it
      // from users who only had delete_project / delete_task.
      if (item.path === '/admin/trash') {
        return canFeatureForRole(normalizedSessionRole, rolePermissions, [
          PERMISSIONS.DELETE_ASSETS,
          PERMISSIONS.DELETE_PROJECT,
          PERMISSIONS.DELETE_TASK,
        ]);
      }
      if (item.path === '/admin/people') {
        return hasPeopleModuleAccess(normalizedSessionRole, rolePermissions);
      }
      if (item.path === '/admin/reports') {
        return hasAnyPermissionForModule(normalizedSessionRole, rolePermissions, 'Reports');
      }

      return true;
    });
  }, [userRole, permissionsReady, referenceDataLoading, rolePermissions, normalizedSessionRole, hasSubordinates]);

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  useEffect(() => {
    const nextState = {};
    menuItems.forEach((item) => {
      if (item.type === 'group' && Array.isArray(item.children)) {
        nextState[item.key || item.label] = item.children.some((child) => isActive(child.path));
      }
    });
    setOpenGroups((prev) => ({ ...nextState, ...prev }));
  }, [location.pathname, menuItems]);

  return (
    <div className={`sidebar ${isOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div className="sidebar-logo">
            <span className="logo-icon">AM</span>
          </div>
          <div className="sidebar-title">
            <h2>Asset Management</h2>
          </div>
        </div>
        {isOpen && (
          <button
            onClick={onClose}
            className="sidebar-close-btn"
            aria-label="Close menu"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '20px',
              flexShrink: 0
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* <div className="sidebar-user-info">
        <div className="user-avatar">
          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
        </div>
        <div className="user-details">
          <div className="user-name">{user.name || 'User'}</div>
          <div className="user-email">{user.email || 'user@example.com'}</div>
        </div>
      </div> */}

      <nav className="sidebar-nav">
        {menuItems.map((item) => {
          if (item.type === 'group' && Array.isArray(item.children)) {
            const groupActive = item.children.some((child) => isActive(child.path));
            const groupKey = item.key || item.label;
            const isGroupOpen = Boolean(openGroups[groupKey]);
            return (
              <div key={item.key || item.label} style={{ marginBottom: '2px' }}>
                <div
                  className={`sidebar-menu-item ${groupActive ? 'active' : ''}`}
                  style={{ cursor: 'pointer', opacity: 0.95, justifyContent: 'space-between' }}
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [groupKey]: !prev[groupKey],
                    }))
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label">{item.label}</span>
                  </span>
                  <span style={{ fontSize: '12px', opacity: 0.9 }}>{isGroupOpen ? '▲' : '▼'}</span>
                </div>
                {isGroupOpen && (
                  <div style={{ marginLeft: '14px' }}>
                    {item.children.map((child) => (
                      <div
                        key={child.path}
                        className={`sidebar-menu-item ${isActive(child.path) ? 'active' : ''}`}
                        onClick={() => handleNavigation(child.path)}
                      >
                        <span className="menu-icon">{child.icon}</span>
                        <span className="menu-label">{child.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div
              key={item.path}
              className={`sidebar-menu-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => handleNavigation(item.path)}
            >
              <span className="menu-icon">{item.icon}</span>
              <span className="menu-label">{item.label}</span>
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-version">v1.0.0</div>
      </div>
    </div>
  );
};

export default Sidebar;
